import { Capacitor } from "@capacitor/core";

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
export const isIOSNative = () => isNative() && Capacitor.getPlatform() === "ios";

let _inited = false;

/** One-time native bootstrap: status bar, splash, keyboard, back button, push. No-op on web. */
export async function initNative() {
  if (_inited || !isNative()) return;
  _inited = true;
  document.documentElement.classList.add("capacitor-native");

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    if (platform() === "android") await StatusBar.setBackgroundColor({ color: "#0A0A0A" });
  } catch (_) {}

  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch (_) {}

  try {
    const { App } = await import("@capacitor/app");
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else App.exitApp();
    });
  } catch (_) {}

  applyPreferredTextSize();
  registerPush();
  captureInstallReferrer();
}

/** Audit F14 : la taille du texte suit le réglage du téléphone (Réglages → Affichage → Taille du texte
 *  sur iOS), y compris quand l'utilisateur la change pendant que l'app est ouverte. Le zoom à deux doigts
 *  reste désactivé (viewport). */
async function applyPreferredTextSize() {
  try {
    const { TextZoom } = await import("@capacitor/text-zoom");
    const apply = async () => {
      const { value } = await TextZoom.getPreferred();
      if (typeof value === "number" && value > 0) await TextZoom.set({ value: Math.min(Math.max(value, 0.85), 2) });
    };
    await apply();
    const { App } = await import("@capacitor/app");
    App.addListener("resume", apply);
  } catch (_) {}
}

/** Play Install Referrer (Android) : lu une seule fois au 1er lancement et
 *  conservé jusqu'à l'inscription (attribution partenaire). No-op si le plugin
 *  natif n'est pas présent / déjà capté. Renseigne 'hitmax_partner_referrer'. */
async function captureInstallReferrer() {
  if (platform() !== "android") return;
  try {
    if (localStorage.getItem("hitmax_referrer_checked")) return;
    localStorage.setItem("hitmax_referrer_checked", "1");
    // On passe par le registre global Capacitor (pas d'import npm) pour rester
    // optionnel : si le plugin natif Install Referrer n'est pas embarqué, no-op.
    const Plugin = (typeof window !== "undefined" && window.Capacitor?.Plugins?.InstallReferrer) || null;
    if (!Plugin || !Plugin.getReferrerDetails) return;
    const res = await Plugin.getReferrerDetails();
    const raw = res?.installReferrer || res?.referrer || "";
    // referrer de la forme "...referrer=CODE..." ou directement "CODE"
    const m = /(?:^|[?&])referrer=([^&]+)/.exec(raw);
    const code = (m ? decodeURIComponent(m[1]) : raw).trim();
    if (code) localStorage.setItem("hitmax_partner_referrer", code);
  } catch (_) {}
}

/** Native push registration. Delivery is managed directly by Firebase/APNs. */
async function registerPush() {
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt") perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return;

    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const url = action?.notification?.data?.url;
      if (url) window.location.href = url;
    });

    await PushNotifications.register();
  } catch (_) {}
}

/** Subscribe to connectivity changes. cb(true|false). Returns a cleanup fn. */
export async function watchNetwork(cb) {
  if (!isNative()) {
    const on = () => cb(navigator.onLine);
    window.addEventListener("online", on);
    window.addEventListener("offline", on);
    on();
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", on);
    };
  }
  try {
    const { Network } = await import("@capacitor/network");
    const status = await Network.getStatus();
    cb(status.connected);
    const handle = await Network.addListener("networkStatusChange", (s) => cb(s.connected));
    return () => handle.remove();
  } catch (_) {
    cb(true);
    return () => {};
  }
}
